import type { PeriodMode } from '../functions/calculations';
import type { AppRoute, HeaderFilterId } from '../routing';
import { getRouteMeta } from '../routing';
import filtersContent from './filters.json';

export interface HeaderFilterDefaults {
  currency: string;
  period: PeriodMode;
}

export interface HeaderFilterDisplayConfig {
  /** Filters that remain visible in the header. */
  pinned: HeaderFilterId[];
  /** Filters revealed by the header filter button. */
  hidden: HeaderFilterId[];
}

const PERIOD_MODES = new Set<PeriodMode>(['ttm', 'ytd', 'all']);

const VALID_FILTER_IDS = new Set<HeaderFilterId>([
  'currency',
  'period',
  'client',
  'unit',
]);

function validFilterIds(values: unknown): HeaderFilterId[] {
  if (!Array.isArray(values)) return [];

  return Array.from(
    new Set(
      values.filter(
        (value): value is HeaderFilterId =>
          typeof value === 'string' && VALID_FILTER_IDS.has(value as HeaderFilterId),
      ),
    ),
  );
}

function normalizePeriodMode(value: unknown): PeriodMode {
  if (typeof value !== 'string') return 'ttm';

  const normalized = value.trim().toLowerCase().replace(/\s+/g, ' ');
  if (normalized === 'all' || normalized === 'all time') return 'all';
  if (normalized === 'ytd' || normalized === 'year to date') return 'ytd';
  if (PERIOD_MODES.has(normalized as PeriodMode)) return normalized as PeriodMode;

  return 'ttm';
}

export function getHeaderFilterDefaults(): HeaderFilterDefaults {
  const defaults = filtersContent.defaults as { currency?: unknown; period?: unknown } | undefined;
  const currency =
    typeof defaults?.currency === 'string' && defaults.currency.trim()
      ? defaults.currency.trim().toUpperCase()
      : 'USD';

  return { currency, period: normalizePeriodMode(defaults?.period) };
}

export function getHeaderFilterDisplayConfig(route: AppRoute): HeaderFilterDisplayConfig {
  const configured = filtersContent[route] as
    | { pinned?: unknown; hidden?: unknown }
    | undefined;

  if (!configured) {
    return {
      pinned: [],
      hidden: [...getRouteMeta(route).headerFilters],
    };
  }

  const pinned = validFilterIds(configured.pinned);
  const pinnedIds = new Set(pinned);

  return {
    pinned,
    hidden: validFilterIds(configured.hidden).filter((filterId) => !pinnedIds.has(filterId)),
  };
}
